import React, { useState, useEffect, useRef } from "react";
import FormInputs from "../../../../../../components/UI/FormInputs";
import Buttons from "../../../../../../components/UI/Buttons";
import UserService from "../../../../../../services/UserService";
import ActivitiesService from "../../../../../../services/ActivitiesService";
import { isSet, getCurrentDate, getCurrentTime, fetchTimeInterval } from "../../../../../../utils/commonUtils";
import PinSignatureSetupModal from "../../../../../../components/common/PinSignatureSetupModal";
import { useSelector } from "react-redux";
import { activeProfileDetails } from "../../../../../../store/SessionSlice";

const ProviderSignOff = ({
  fields = [],
  formData = {},
  changeHandler,
  Toast,
  Loader,
  readOnly,
  validation = {},
  activityId,
  pinFieldName = "provider_pin",
  signatureFieldName = "provider_signature",
  authorizedNameFieldName = "authorized_name",
  signedDateFieldName = "signed_date",
  signedTimeFieldName = "signed_time",
  authorizedNameLabel = "Authorized Name",
  signedDateLabel = "Date",
  signedTimeLabel = "Time",
  showAuthorizedFieldsAlways = false,
  required_authority = null,
}) => {
  const [verifying, setVerifying] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [authorizedName, setAuthorizedName] = useState("");
  const [fetchedRequiredAuthority, setFetchedRequiredAuthority] = useState(null);
  const prevPinLengthRef = useRef(0);
  const profileDetails = useSelector(activeProfileDetails);
  const pinField = fields.find((f) => f.name === pinFieldName);
  const signatureField = fields.find((f) => f.name === signatureFieldName);
  // Compute early for use in effects (effectiveRequiredAuthority may still be from fetch)
  const effectiveRequiredAuthorityForEffect = required_authority !== null && required_authority !== undefined
    ? required_authority
    : fetchedRequiredAuthority;
  const isPinVerificationRequiredForEffect = effectiveRequiredAuthorityForEffect !== null &&
    effectiveRequiredAuthorityForEffect !== undefined &&
    !(Array.isArray(effectiveRequiredAuthorityForEffect) && effectiveRequiredAuthorityForEffect.length === 0) &&
    effectiveRequiredAuthorityForEffect !== '';
  // Get additional fields (excluding PIN, Signature, and Authorized Name)
  // Note: Date field (signed_date) is included in additionalFields to be rendered.
  // If signed_time is not provided by chart config, auto-insert it after signed_date
  // to keep Review / Completion structure consistent across activity types.
  const rawAdditionalFields = fields.filter(
    (f) => f.name !== pinFieldName && f.name !== signatureFieldName && f.name !== authorizedNameFieldName
  );
  const hasSignedTimeField = rawAdditionalFields.some((f) => f.name === signedTimeFieldName);
  const additionalFields = hasSignedTimeField
    ? rawAdditionalFields
    : rawAdditionalFields.flatMap((field) => {
        if (field.name !== signedDateFieldName) return [field];
        const normalizedDateField =
          String(field.col || "").trim() === "col-12"
            ? { ...field, col: "col-6" }
            : field;
        return [
          normalizedDateField,
          {
            type: "SelectSearchList",
            label: signedTimeLabel,
            placeholder: signedTimeLabel,
            isAsync: true,
            options: [{ label: getCurrentTime(), value: getCurrentTime() }],
            loadCallBack: (val, callback) => fetchTimeInterval(val, callback),
            name: signedTimeFieldName,
            col: "col-6",
          },
        ];
      });
  // Fetch activity by activityId if required_authority is not provided
  useEffect(() => {
    let isMounted = true;
    const fetchActivityRequiredAuthority = async () => {
      // If required_authority is already provided, use it
      if (required_authority !== null && required_authority !== undefined) {
        if (isMounted) {
          setFetchedRequiredAuthority(required_authority);
        }
        return;
      }
      // If activityId is available but required_authority is missing, fetch it
      if (activityId) {
        try {
          if (Loader) Loader(true);
          const activity = await ActivitiesService.getActivitiesById(activityId);
          if (activity && isMounted) {
            // Handle both array and comma-separated string formats
            let authority = activity.required_authority;
            if (typeof authority === 'string' && authority.trim() !== '') {
              authority = authority.split(',').map(a => a.trim()).filter(a => a);
            }
            setFetchedRequiredAuthority(authority || null);
          }
        } catch (e) {
          console.error('Error fetching activity required_authority:', e);
          if (isMounted) {
            setFetchedRequiredAuthority(null);
          }
        } finally {
          if (Loader) Loader(false);
        }
      } else if (isMounted) {
        setFetchedRequiredAuthority(null);
      }
    };
    fetchActivityRequiredAuthority();
    return () => {
      isMounted = false;
    };
  }, [activityId, required_authority]);
  
  // When loading existing chart data, trust DB values only.
  // If name is missing but signoff_user_id exists, resolve signer name from user record.
  useEffect(() => {
    let isMounted = true;

      const resolveAuthorizedName = async () => {
      // Use effective required_authority (from prop or fetched) - use the one defined at component level
      const currentEffectiveRequiredAuthority = required_authority !== null && required_authority !== undefined 
        ? required_authority 
        : fetchedRequiredAuthority;
      
      // Check if PIN verification is required
      const isPinVerificationRequired = currentEffectiveRequiredAuthority !== null && 
        currentEffectiveRequiredAuthority !== undefined && 
        !(Array.isArray(currentEffectiveRequiredAuthority) && currentEffectiveRequiredAuthority.length === 0) &&
        currentEffectiveRequiredAuthority !== '';

      // If PIN verification is not required, we don't need to verify PIN
      if (!isPinVerificationRequired) {
        if (isMounted) {
          setPinVerified(true); // Allow showing authorized fields without PIN
          // Try to get authorized name from formData
          const dbName = formData?.authorized_name || formData?.user_name || "";
          if (dbName) {
            setAuthorizedName(dbName);
          } else {
            // If no name in formData, try to get from profile or signoff_user_id
            const signoffUserId = formData?.signoff_user_id;
            if (signoffUserId) {
              try {
                const user = await UserService.getUserById(signoffUserId);
                const resolvedName =
                  user?.username ||
                  user?.name ||
                  (user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.first_name || user?.last_name || "");
                if (isMounted) setAuthorizedName(resolvedName);
              } catch (e) {
                if (isMounted) setAuthorizedName("");
              }
            } else if (formData?.[signatureFieldName]) {
              // If signature exists but no name, use profile name
              const name = profileDetails?.username || profileDetails?.name ||
                (profileDetails?.first_name && profileDetails?.last_name
                  ? `${profileDetails.first_name} ${profileDetails.last_name}`
                  : '');
              if (isMounted) setAuthorizedName(name);
            }
          }
        }
        return;
      }

      // PIN verification is required - use existing logic
      if (!formData?.[signatureFieldName]) {
        if (isMounted) {
          setPinVerified(false);
          setAuthorizedName("");
        }
        return;
      }

      if (isMounted) {
        setPinVerified(true);
      }

      const dbName = formData?.authorized_name || formData?.user_name || "";
      if (dbName) {
        if (isMounted) setAuthorizedName(dbName);
        return;
      }

      const signoffUserId = formData?.signoff_user_id;
      if (!signoffUserId) {
        if (isMounted) setAuthorizedName("");
        return;
      }

      try {
        const user = await UserService.getUserById(signoffUserId);
        const resolvedName =
          user?.username ||
          user?.name ||
          (user?.first_name && user?.last_name
            ? `${user.first_name} ${user.last_name}`
            : user?.first_name || user?.last_name || "");
        if (isMounted) setAuthorizedName(resolvedName);
      } catch (e) {
        if (isMounted) setAuthorizedName("");
      }
    };

    resolveAuthorizedName();
    return () => {
      isMounted = false;
    };
  }, [
    formData?.[signatureFieldName],
    formData?.authorized_name,
    formData?.user_name,
    formData?.signoff_user_id,
    required_authority,
    fetchedRequiredAuthority,
    profileDetails
  ]);

  const handleVerifyPin = async () => {
    const pin = formData?.[pinFieldName] || "";
    if (!pin.trim()) {
      Toast?.error?.("Please enter PIN");
      return;
    }
    setVerifying(true);
    if (Loader) Loader(true);
    try {
      const result = await UserService.verifyPin(pin, activityId);
      if (result?.success && result?.signature) {
        changeHandler?.(signatureFieldName, result.signature);
        // Set signed_date to current date when PIN is verified (format: MM/DD/YYYY)
        const currentDate = getCurrentDate();
        const currentTime = getCurrentTime();
        changeHandler?.(signedDateFieldName, currentDate);
        changeHandler?.(signedTimeFieldName, currentTime);
        // Set PIN verified state and authorized name (persist so payload includes it)
        setPinVerified(true);
        const name = profileDetails?.username || profileDetails?.name ||
          (profileDetails?.first_name && profileDetails?.last_name
            ? `${profileDetails.first_name} ${profileDetails.last_name}`
            : '');
        setAuthorizedName(name);
        changeHandler?.(authorizedNameFieldName, name);
        Toast?.success?.("Signature loaded successfully");
      } else {
        Toast?.error?.(result?.message || "Invalid PIN");
      }
    } catch (err) {
      let msg = "Invalid PIN or user not found";
      if (err?.message) msg = err.message;
      else if (err?.data?.message) msg = err.data.message;
      else if (typeof err?.data === "string") msg = err.data;
      else if (err?.errors?.pin && Array.isArray(err.errors.pin)) msg = err.errors.pin[0];
      if (err?.data?.needs_setup) {
        setShowSetup(true);
        msg = "PIN not set. Please set PIN & Signature.";
      }
      Toast?.error?.(msg);
    } finally {
      setVerifying(false);
      if (Loader) Loader(false);
    }
  };

  // Auto-submit PIN when 4 digits are entered (only when transitioning to 4 digits)
  useEffect(() => {
    const pin = String(formData?.[pinFieldName] ?? "").replace(/\D/g, "");
    const len = pin.length;
    const prevLen = prevPinLengthRef.current;
    prevPinLengthRef.current = len;
    if (
      isPinVerificationRequiredForEffect &&
      !readOnly &&
      !verifying &&
      len === 4 &&
      prevLen === 3
    ) {
      handleVerifyPin();
    }
  }, [formData?.[pinFieldName], readOnly, verifying, isPinVerificationRequiredForEffect]);

  if (!pinField || !signatureField) return null;

  // Use effective required_authority (from prop or fetched)
  const effectiveRequiredAuthority = required_authority !== null && required_authority !== undefined 
    ? required_authority 
    : fetchedRequiredAuthority;
  // Check if PIN verification is required based on required_authority
  const isPinVerificationRequired = effectiveRequiredAuthority !== null && 
    effectiveRequiredAuthority !== undefined && 
    !(Array.isArray(effectiveRequiredAuthority) && effectiveRequiredAuthority.length === 0) &&
    effectiveRequiredAuthority !== '';

  // Format date from MM/DD/YYYY to MM-DD-YYYY for display
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    // If date is in MM/DD/YYYY format, convert to MM-DD-YYYY
    if (dateStr.includes('/')) {
      return dateStr.replace(/\//g, '-');
    }
    // If date is already in YYYY-MM-DD format, convert to MM-DD-YYYY
    if (dateStr.includes('-') && dateStr.length === 10) {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        return `${parts[1]}-${parts[2]}-${parts[0]}`;
      }
    }
    return dateStr;
  };

  const signedDate = formData?.[signedDateFieldName] || '';
  const signedTime = formData?.[signedTimeFieldName] || '';
  const formattedDate = formatDateForDisplay(signedDate);

  return (
    <>
      <PinSignatureSetupModal
        isOpen={showSetup}
        onClose={() => setShowSetup(false)}
        Toast={Toast}
        Loader={Loader}
        onSave={async ({ pin, signature }) => {
          const res = await UserService.setPinSignature(pin, signature);
          // pre-fill signature in chart after saving
          changeHandler?.(signatureFieldName, "");
          // clear any previously entered (possibly invalid) PIN in the chart after setup
          changeHandler?.(pinFieldName, "");
          return res;
        }}
      />
      <div className="row align-items-start">
        <div className="col-md-6">
          {/* PIN row */}
          {isPinVerificationRequired && (
          <div className="form-group mb-2 p-2">
            <label className="d-block f-400 text-sm mb-1 opacity-75">
              {pinField.label}
            </label>
            <div className="d-flex align-items-center gap-2">
              <div className="flex-grow-1">
                <FormInputs
                  {...pinField}
                  //type="Password"
                  type="TextInput"
                  placeholder="Enter PIN (4 digits)"
                  value={formData?.[pinFieldName] || ""}
                  changeHandler={(val) => {
                    const digitsOnly = String(val ?? "").replace(/\D/g, "").slice(0, 4);
                    changeHandler?.(pinFieldName, digitsOnly);
                  }}
                  inputProps={{ maxLength: 4 }}
                  onKeyPressHandler={(e) => {
                    if (["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
                    if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key?.toLowerCase())) return;
                    const current = String(formData?.[pinFieldName] || "");
                    if (!/^\d$/.test(e.key) || current.length >= 4) e.preventDefault();
                  }}
                  validation={validation}
                  disabled={readOnly}
                />
              </div>
            </div>
            {isSet(validation?.[pinFieldName], "") !== "" && (
              <span className="text-danger m-1 d-block">
                {validation?.[pinFieldName]}
              </span>
            )}
          </div>
          )}
          {/* Signature row */}
          <div className="form-group mb-0 p-2">
            <label className="d-block f-400 text-sm mb-1 opacity-75">
              {signatureField.label}
            </label>
            <div>
              <FormInputs
                {...signatureField}
                label=""
                type="NewSignature"
                value={formData?.[signatureFieldName] || ""}
                changeHandler={(val) => {
                  changeHandler?.(signatureFieldName, val);
                  // When PIN verification is not required and signature is added, auto-populate name and date
                  if (!isPinVerificationRequired && val) {
                    const name = profileDetails?.username || profileDetails?.name ||
                      (profileDetails?.first_name && profileDetails?.last_name
                        ? `${profileDetails.first_name} ${profileDetails.last_name}`
                        : '');
                    if (name && !formData?.[authorizedNameFieldName]) {
                      setAuthorizedName(name);
                      changeHandler?.(authorizedNameFieldName, name);
                    }
                    if (!formData?.[signedDateFieldName]) {
                      const currentDate = getCurrentDate();
                      changeHandler?.(signedDateFieldName, currentDate);
                    }
                    if (!formData?.[signedTimeFieldName]) {
                      const currentTime = getCurrentTime();
                      changeHandler?.(signedTimeFieldName, currentTime);
                    }
                  }
                }}
                validation={validation}
                disabled={isPinVerificationRequired}
                readOnly={isPinVerificationRequired}
              />
              {isSet(validation?.[signatureFieldName], "") !== "" && (
                <span className="text-danger m-1 d-block">
                  {validation?.[signatureFieldName]}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right side: Additional Fields (Date, Suggested Result, Final Results, Activity Status) */}
        <div className="col-md-6">
          <div className="row">
            {/* Render additional fields (Date, Suggested Result, Final Results, Activity Status) */}
            {additionalFields.map((field, index) => {
              const fieldValue = formData?.[field.name] || "";
              const fieldValidation = validation?.[field.name] || "";
              const fieldCol = field.col || "col-12";
              const activityStatusValue = String(formData?.activity_status || "").toLowerCase().trim();
              const hasProviderSignature = !!(formData?.[signatureFieldName] !== "" && formData?.[signatureFieldName] !== null && formData?.[signatureFieldName] !== undefined);
              const shouldHideDefaultActivityStatusWarning =
                // (activityStatusValue === "completed" || activityStatusValue === "cancelled") &&
                hasProviderSignature;
              const defaultActivityStatusSubLabel =
                field.name === "activity_status" && typeof field.subLabel === "undefined"
                  ? (shouldHideDefaultActivityStatusWarning ? "" : "Signature is required to complete the activity status")
                  : "";
              const resolvedSubLabel = typeof field.subLabel === "function"
                ? field.subLabel(formData, field)
                : isSet(field.subLabel, defaultActivityStatusSubLabel);
              
              return (
                <div key={field.name || index} className={fieldCol}>
                  <div className="form-group mb-2">
                    <label className="d-block f-400 text-sm mb-1 opacity-75">
                      {field.label}
                    </label>
                    {isSet(resolvedSubLabel, "") !== "" ? (
                      <div className={isSet(field.subLabelClassName, "text-danger text-xs mb-1")}>
                        {resolvedSubLabel}
                      </div>
                    ) : null}
                    <FormInputs
                      {...field}
                      type={field.type || "TextInput"}
                      value={fieldValue}
                      changeHandler={(val) => changeHandler?.(field.name, val)}
                      validation={validation}
                      disabled={readOnly || field.disabled}
                      readOnly={readOnly || field.readOnly}
                      options={field.options || []}
                    />
                    {isSet(fieldValidation, "") !== "" && (
                      <span className="text-danger m-1 d-block">
                        {fieldValidation}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}    
          </div>
        </div>
      </div>
      {/* Bottom section: Printed Name and Signature Date - Display as text labels like Figma */}
      {(!isPinVerificationRequired || showAuthorizedFieldsAlways || pinVerified || formData?.[signatureFieldName]) && (
        <div className="row mt-3">
          <div className="col-12">
            {authorizedName && (
              <div className="mb-2 px-2">
                <span className="text-dark">
                  Printed Name: <strong>{authorizedName}</strong>
                </span>
              </div>
            )}
            {formattedDate && (
              <div className="px-2">
                <span className="text-dark">
                  {`Signature ${signedDateLabel}: `}<strong>{formattedDate}</strong>
                </span>
              </div>
            )}
            {signedTime && (
              <div className="px-2 mt-1">
                <span className="text-dark">
                  {`Signature ${signedTimeLabel}: `}<strong>{signedTime}</strong>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProviderSignOff;
